import { Fragment } from "react";
import Head from "next/head";

import ApplicationLayout from "components/ApplicationLayout";
import Navbar from "components/Navbar";
import PageContent from "components/PageContent";
import SharedFooter from "components/SharedFooter";
import ReadyForTakeOff from "components/ReadyForTakeOff";
import SocialFooter from "components/SocialFooter";
import StoriesWrapper from "components/community-stories/StoriesWrapper";
import StoryCard from "components/community-stories/StoryCard";
import AddStory from "components/community-stories/AddStory";

import Container from "components/Container";

import { getAllCommunityStories } from "lib/remoteContent";

import styles from "./index.module.css";

import type { GetStaticProps } from "next";
import type { Page } from "types/page";
import type { CommunityStory } from "lib/remoteContent";

const ADD_STORY_POSITION = 7;

type PageProps = {
  stories: CommunityStory[];
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  // randomize order of stories
  const stories = (await getAllCommunityStories())
    .map((story) => ({ story, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ story }) => story);
  return {
    props: {
      stories,
    },
  };
};

const CommunityStories: Page<PageProps> = ({ stories }) => {
  return (
    <>
      <Head>
        <title>Raycast Community Stories</title>
        <meta
          property="og:image"
          key="og-image"
          content={`https://${process.env.NEXT_PUBLIC_VERCEL_URL}/community-stories/og-image.png`}
        />
        <meta property="og:title" content="Raycast Community Stories" />
        <meta
          property="og:description"
          content={
            "Hear from folks in the community who use Raycast in their daily workflow to empower them to do their best work."
          }
        />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="512" />
        <meta property="twitter:card" content="summary_large_image" />
      </Head>
      <div className={styles.wrapper}>
        <h1 className={styles.header}>Community Stories</h1>
        <p className={styles.explainer}>
          Hear from folks in the community who use Raycast in their daily workflow to empower them to do their best
          work.
        </p>
        <StoriesWrapper>
          {stories.map((story, index) => (
            <Fragment key={story.name}>
              <StoryCard story={story} />
              {index === ADD_STORY_POSITION ? <AddStory /> : null}
            </Fragment>
          ))}
        </StoriesWrapper>
      </div>
    </>
  );
};

CommunityStories.getLayout = (page) => {
  return (
    <ApplicationLayout>
      <Navbar />
      <PageContent>
        <Container>
          {page}
          <ReadyForTakeOff />
          <SocialFooter />
        </Container>
      </PageContent>
      <SharedFooter />
    </ApplicationLayout>
  );
};

export default CommunityStories;
